import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Input, Space, Table, Typography, DatePicker, Button, message,
} from 'antd';
import { SearchOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  OneCServiceOrder,
  getOneCServiceOrders,
  createOneCServiceOrder,
} from '../api/onec';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const OneCServiceOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OneCServiceOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);
  const [counterpartyName, setCounterpartyName] = useState('');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOneCServiceOrders({
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...(counterpartyName.trim() ? { counterparty_name: counterpartyName.trim() } : {}),
      });
      setOrders(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить заказы из 1С');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, counterpartyName]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleCreateTest = async () => {
    try {
      const newOrder = await createOneCServiceOrder({
        counterparty: 'Тестовый контрагент',
        license_plate: 'А123БВ51',
        mileage: 50000,
        products: [
          {
            nomenclature: 'Масло моторное',
            quantity: 2,
            price: 1500,
            sum: 3000,
          },
        ],
        services: [
          {
            service_name: 'Замена масла',
            quantity: 1,
            sum: 500,
          },
        ],
      });
      message.success(`Создан заказ ${newOrder.number}`);
      loadOrders();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка создания заказа');
    }
  };

  const handleDateChange = (dates: any) => {
    if (dates && dates[0]) {
      setDateFrom(dates[0].format('YYYY-MM-DD'));
    } else {
      setDateFrom(undefined);
    }
    if (dates && dates[1]) {
      setDateTo(dates[1].format('YYYY-MM-DD'));
    } else {
      setDateTo(undefined);
    }
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'number',
      key: 'number',
      width: 120,
      render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      render: (v: string) => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: 'Контрагент',
      dataIndex: 'counterparty',
      key: 'counterparty',
    },
    {
      title: 'Менеджер',
      dataIndex: 'manager',
      key: 'manager',
      width: 160,
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (v: number) => Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 160,
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_: any, record: OneCServiceOrder) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/1c-service-orders/${record.id}`)}
        />
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Заказ-наряды 1С</Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            placeholder={['Дата с', 'Дата по']}
            onChange={handleDateChange}
            format="DD.MM.YYYY"
          />
          <Input
            placeholder="Поиск по контрагенту"
            prefix={<SearchOutlined />}
            value={counterpartyName}
            onChange={e => setCounterpartyName(e.target.value)}
            style={{ width: 280 }}
            allowClear
            onPressEnter={loadOrders}
          />
          <Button type="primary" onClick={loadOrders} loading={loading}>
            Обновить
          </Button>
          <Button icon={<PlusOutlined />} onClick={handleCreateTest}>
            Создать тестовый
          </Button>
        </Space>
      </Card>

      <Table
        dataSource={orders}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        size="small"
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Нет заказов' }}
        onRow={(record) => ({
          onClick: () => navigate(`/1c-service-orders/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default OneCServiceOrdersPage;
